import { roomPath } from '../../shared/routes';
import { copyText } from './clipboard';

/** Browser-owned invite sharing: system sheet where available, clipboard fallback otherwise. */

export interface ShareInvite {
  url: string;
  title?: string;
  text?: string;
}

export type ShareOutcome = 'shared' | 'copied' | 'dismissed' | 'failed';

/** Full invite URL for a room code; owns the `window.location` read for its callers. */
export function inviteUrl(code: string): string {
  return new URL(roomPath(code), window.location.origin).href;
}

export function canSystemShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * Open the system share sheet when the browser offers one, otherwise copy the URL.
 * A dismissed sheet stays silent; only a failed copy reports `failed`. A share
 * rejection that is not a dismissal falls through to the clipboard so desktop
 * browsers without a share target still copy the link.
 */
export async function shareInvite(input: ShareInvite): Promise<ShareOutcome> {
  if (canSystemShare()) {
    try {
      await navigator.share({
        url: input.url,
        ...(input.title ? { title: input.title } : {}),
        ...(input.text ? { text: input.text } : {}),
      });
      return 'shared';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'dismissed';
    }
  }
  try {
    await copyText(input.url);
    return 'copied';
  } catch {
    return 'failed';
  }
}
